import { Injectable, NgZone } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    BehaviorSubject,
    Subject,
    Observable,
    fromEvent,
    merge
} from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    tap,
    finalize,
    takeUntil,
    filter,
    startWith,
    share,
    exhaustMap,
    shareReplay,
    withLatestFrom
} from 'rxjs/operators';

export interface PagedAutocompleteController<T> {
    results$: Observable<T[]>;
    isLoading$: Observable<boolean>;
    /** Carregando a próxima página (rolagem), não uma busca nova */
    isLoadingMore$: Observable<boolean>;
    incrementPage: () => void;
    setQuery: (q: string) => void;
    reset: () => void;
    notifyOpened: () => void;
    destroy: () => void;
}

@Injectable({ providedIn: 'root' })
export class PagedAutocompleteService {
    constructor(private zone: NgZone) { }

    create<T>(
        control: FormControl,
        searchFn: (query: string, page: number) => Observable<{ data?: T[] }>,
        panelElementProvider: () => HTMLElement | null,
        openNotifier?: Observable<void>
    ): PagedAutocompleteController<T> {
        const destroy$ = new Subject<void>();
        const page$ = new BehaviorSubject<number>(1);
        const results$ = new BehaviorSubject<T[]>([]);
        const isLoading$ = new BehaviorSubject<boolean>(false);
        const isLoadingMore$ = new BehaviorSubject<boolean>(false);
        const firstPageReady$ = new BehaviorSubject<boolean>(false);
        const hasMoreData$ = new BehaviorSubject<boolean>(true);
        const accumulatedResults: T[] = [];

        const open$ = (openNotifier ?? new Subject<void>()).pipe(takeUntil(destroy$));

        const controlQuery$ = control.valueChanges.pipe(
            startWith(control.value || ''),
            debounceTime(300),
            map(val => (typeof val === 'string' ? val : '')),
            distinctUntilChanged(),
            share(),
            takeUntil(destroy$)
        );

        const trigger$ = merge(
            // Digitação do usuário: reseta a paginação e usa a nova query.
            controlQuery$.pipe(
                // Removido o filtro `q.trim().length > 0`
                tap(() => {
                    page$.next(1);
                    // scrolle para o topo quando a query for alterada
                    const panel = panelElementProvider();
                    if (panel) {
                        panel.scrollTop = 0;
                    }
                }),
                map(query => ({ query, page: 1 }))
            ),
            // Scroll do usuário: usa a query atual e incrementa a página.
            page$.pipe(
                withLatestFrom(controlQuery$, hasMoreData$),
                filter(([page, query, hasMoreData]) => hasMoreData && page > 1),
                map(([page, query]) => ({ query, page }))
            )
        ).pipe(share());

        trigger$
            .pipe(
                exhaustMap(({ query, page }) => {
                    isLoading$.next(true);
                    isLoadingMore$.next(page > 1);
                    return searchFn(query, page).pipe(
                        map(resp => resp.data ?? []),
                        tap(items => {
                            if (page === 1) {
                                accumulatedResults.length = 0;
                                firstPageReady$.next(false);
                                hasMoreData$.next(true);
                            }
                            if (items.length === 0) {
                                hasMoreData$.next(false);
                            }
                            accumulatedResults.push(...items);
                            results$.next([...accumulatedResults]);
                            if (page === 1) {
                                firstPageReady$.next(true);
                            }
                        }),
                        finalize(() => {
                            isLoading$.next(false);
                            isLoadingMore$.next(false);
                        }),
                        shareReplay(1)
                    );
                }),
                takeUntil(destroy$)
            )
            .subscribe();

        open$.pipe(
            debounceTime(50),
            tap(() => {
                const panel = panelElementProvider();
                if (!panel) return;
                this.zone.runOutsideAngular(() => {
                    fromEvent(panel, 'scroll')
                        .pipe(
                            takeUntil(destroy$),
                            filter(() => firstPageReady$.value && !isLoading$.value),
                            filter(() => {
                                const scrollableHeight = panel.scrollHeight - panel.clientHeight;
                                return panel.scrollTop >= scrollableHeight - 10;
                            }),
                            tap(() => this.zone.run(() => page$.next(page$.value + 1))),
                        )
                        .subscribe();
                });
            })
        ).subscribe();

        function resetSearchState() {
            page$.next(1);
            accumulatedResults.length = 0;
            results$.next([]);
            firstPageReady$.next(false);
            hasMoreData$.next(true);
        }

        return {
            results$: results$.asObservable(),
            isLoading$: isLoading$.asObservable(),
            isLoadingMore$: isLoadingMore$.asObservable(),
            incrementPage: () => page$.next(page$.value + 1),
            setQuery: q => control.setValue(q),
            reset: () => {
                control.setValue('');
                resetSearchState();
            },
            notifyOpened: () => {
                const subj = openNotifier as Subject<void> | undefined;
                subj?.next();
            },
            destroy: () => {
                destroy$.next();
                destroy$.complete();
            }
        };
    }
}